import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  Button,
  Stack,
  Box,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { BaseFormComponent, FormTemplate } from '../types';
import {
  getTemplateDomainLabel,
  getDifficultyInfo,
} from '../helpers';
// This dialog shows BOTH vocabularies at once: the template's business domain in the
// header, and each included component's field family in the body. That is why the split
// had to be per call site rather than per page — one function serving both is how
// "Financial" (a currency field) ended up beside "Finance" (a department).
import ProcessFormPreview from '../../../../components/process/ProcessFormPreview';
import { getFormComponentDisplay, getFormTemplateDisplay } from '../../processDisplay';

interface TemplateDialogProps {
  open: boolean;
  template: FormTemplate | null;
  onClose: () => void;
  onOpenComponent: (component: BaseFormComponent) => void;
  allComponents: BaseFormComponent[];
  onDelete: (template: FormTemplate) => void;
  /** Open the editor for this template.
   *
   * Gated on `!isSystem` beside the delete, and for the same reason recorded there: the backend
   * refuses to modify a seeded template, so a button that always 403s reads as a broken product
   * rather than as a protected row. */
  onEdit: (template: FormTemplate) => void;
}

const TemplateDialog: React.FC<TemplateDialogProps> = ({
  open,
  template,
  onClose,
  onOpenComponent,
  onDelete,
  onEdit,
  allComponents,
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const display = template ? getFormTemplateDisplay(t, template) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ m: 0, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{t('processes:formComponents.templateDialog.title', { name: display?.name })}</Typography>
        <IconButton aria-label={t('common:actions.close')} onClick={onClose} sx={{ color: (t) => t.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ py: 3 }}>
        {template && (
          <Box>
            {/* HEADER, rewritten 2026-08-27 (owner: "非常的丑陋，不紧凑").
                What it was: a 56px colour-filled Avatar, an `h5` name, two filled chips in
                tenant-domain colours, a `body1` paragraph, then a two-column Grid of
                label-over-value pairs — roughly 240px of chrome before the content.

                Four svdy-ui rules, in one dialog:
                  §1  headers are `h6`/600, not `h5`; chips are outlined and 18-22px, not
                      filled blocks in invented colours
                  §2  no giant summary block above the actual content
                  §6  palette tokens only — `getTemplateDomainColor` mints its own
                  §15 one alignment axis: the Grid's label-over-value pairs started at a
                      third origin from everything else

                Now: one line of identity, one line of facts. The facts are a middot-joined
                `body2` stats line — the §3 pattern every list page in this app already
                uses — instead of a Grid that needed two rows to say two short things. */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
                {display?.name}
              </Typography>
              <Chip
                label={getTemplateDomainLabel(t, template.category)}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1 }}
              />
              <Chip
                label={getDifficultyInfo(t, theme, template.difficulty).label}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1 }}
              />
            </Stack>
            {display?.description && (
              <Typography variant="body2" color="text.secondary">
                {display.description}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              {/* NO literal `:` after either label. The nine locale strings already end in
                  one — and in `：`, the full-width form, for zh-CN / ja, which an ASCII
                  colon appended here would get wrong anyway. Appending it produced
                  "Estimated Setup Time:: 30 min · Last Updated:: 8/28/2026" (owner
                  screenshots 2026-08-28). Punctuation belongs to the locale. */}
              {t('processes:formComponents.templateDialog.estimatedSetupTime')}{' '}
              <strong>{template.estimatedTime}</strong>
              {' · '}
              {t('processes:formComponents.templateDialog.lastUpdated')}{' '}
              <strong>{new Date(template.lastUpdated).toLocaleDateString(i18n.language)}</strong>
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('processes:formComponents.templateDialog.includedComponents', { count: template.items.length })}
            </Typography>

            {/* THE REAL read-only renderer, not a decorative list (owner: "没有在 form 表单
                中的那个显示的预览效果").

                What it was: one outlined Card per component, each holding a Chip that held a
                28px colour-filled Avatar, plus a second colour-outlined Chip on the right.
                Three levels of border for one field name, in colours minted by
                `getFieldFamilyColor` rather than palette tokens — svdy-ui §15's "no card
                inside a card", §6's "palette tokens only", and it still did not show what
                the FORM will look like.

                `ProcessFormPreview` is the same component the Designer's Review step uses,
                so the admin sees here exactly what the requester will see. One renderer, so
                the two cannot drift (§10.5b).

                Names are RESOLVED first: a seeded component's label is localized through
                `getFormComponentDisplay`, which a raw pass of `comp.label` would skip. */}
            <ProcessFormPreview
              schema={{
                fields: template.items
                  .map((item) => ({
                    item,
                    comp: allComponents.find((c) => c.id === item.componentId),
                  }))
                  .filter((pair): pair is { item: typeof pair.item; comp: NonNullable<typeof pair.comp> } => !!pair.comp)
                  .map(({ item, comp }) => ({
                    id: comp.id,
                    // `fieldType`, not `field_type`: this page's components are the
                    // MAPPED shape, not the raw API row. TypeScript caught the guess.
                    type: comp.fieldType,
                    label: getFormComponentDisplay(t, comp).label,
                    // This page's `properties` is a DESCRIPTOR ARRAY (name/label/type/
                    // defaultValue), not the runtime object the renderer wants — which is
                    // also why the old decorative list could not show a real preview.
                    // Folded to defaults the same way `WizardFormBuilder`'s catalog mapper
                    // does, so the two produce the same field (§10.5b), including the
                    // newline-separated `options` string a dropdown declares.
                    properties: {
                      ...Object.fromEntries(
                        (comp.properties ?? [])
                          .filter((property) => property.defaultValue !== undefined)
                          .map((property) => [
                            property.name,
                            property.name === 'options' && typeof property.defaultValue === 'string'
                              ? property.defaultValue.split('\n')
                              : property.defaultValue,
                          ]),
                      ),
                      // LAST, so no component-level descriptor can outrank it. The width
                      // is a property of the template ROW, not of the component: the same
                      // component sits Half in one template and Full in another, which is
                      // exactly why it cannot be folded in with the defaults above.
                      //
                      // This line is the fix for the owner's screenshots. Everything else
                      // in this pipeline already worked: the dialog stored the span, the
                      // migration persisted it, the API returned it, and
                      // `ProcessFormPreview` laid out a 12-column grid for it. The span
                      // simply never arrived here.
                      colSpan: item.colSpan,
                    },
                  })),
              }}
            />

            {/* An id in the template that no longer resolves to a component. Reported rather
                than skipped: a template quietly rendering fewer fields than it lists is how
                a deleted component becomes invisible. */}
            {template.items
              .filter((item) => !allComponents.some((c) => c.id === item.componentId))
              .map((item) => (
                <Typography key={item.componentId} variant="body2" color="error.main" sx={{ mt: 1 }}>
                  {t('processes:formComponents.templateDialog.unknownComponent', { id: item.componentId })}
                </Typography>
              ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        {/* Symmetric with ComponentDialog: the delete lives at `mr: 'auto'` on the left,
            and a SYSTEM row is not offered one at all. `delete_form_template` returns 403
            on `is_system`, so a button there would always fail — worse than no button,
            because it reads as a broken product rather than as a protected row.

            `template &&` is required because this dialog keeps its shell mounted and
            guards only its BODY (`{template && …}` above), whereas ComponentDialog
            early-returns `if (!component) return null`. Two null conventions in one
            folder; this follows the local one rather than changing the sibling's, and
            omitting it crashed two existing tests on `null.isSystem`. */}
        {template && !template.isSystem && (
          <Button color="error" startIcon={<DeleteIcon />} onClick={() => onDelete(template)} sx={{ mr: 'auto' }}>
            {t('common:actions.delete')}
          </Button>
        )}
        {/* EDIT — the entry point that did not exist. `PATCH /form-templates/{id}` and
            `formTemplatesApi.update` have shipped since migration 111 with zero call sites, so
            changing one field's width meant deleting the template and rebuilding it.
            Deliberately HERE and not in the Designer: the Designer's job is to consume a
            template, and its four-step wizard already serves one goal. Template management has
            its own page with the list, the categories and the usage counts — the edit belongs
            beside them. */}
        {template && !template.isSystem && (
          <Button startIcon={<EditIcon />} onClick={() => onEdit(template)}>
            {t('common:actions.edit')}
          </Button>
        )}
        <Button onClick={onClose} variant="contained">{t('common:actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateDialog;
